import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { enqueueSnackbar } from 'notistack'
import { IconFolderUp, IconUpload, IconX } from '@tabler/icons-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Dialog, DialogOverlay, DialogPortal } from '../../atoms/dialog'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { GradientBorderButton } from '../../atoms/buttons/GradientBorderButton'
import Typography from '../../atoms/Typography'
import { Project, DataRoomDocument, DataRoomFolder } from '../../../types/projects'
import { api } from '../../../api/root'
import useAuth from '../../../enviroment/hooks/useAuth'
import { useTenantStore } from '../../../enviroment/store/tenentStore'
import { SubscriptionPlanType } from '../../../types/subscription'
import DataRoomSidebar from './DataRoomSidebar'
import DataRoomDocumentsList from './DataRoomDocumentsList'
import DataRoomFileDropzone, { DataRoomFileDropzoneHandle } from './DataRoomFileDropzone'
import DataRoomDocumentDetail from './DataRoomDocumentDetail'
import { DataRoomActionPanel } from './DataRoomActionPanel'
import MoveToFolderModal from './MoveToFolderModal'
import DeleteFolderModal from './DeleteFolderModal'
import UploadFolderModal from './UploadFolderModal'
import StorageLimitExceededDialog from '../dialogs/StorageLimitExceededDialog'
import { folderNameSchema } from './folderNameSchema'
import { formatFileSize } from '../../../enviroment/lib/formatters/format-file-size'
import { ROUTES } from '../../../types/routes'
import { joinFolderPath } from '../../../enviroment/utils/joinFolderPath'
import { parentFolderPath } from '../../../enviroment/utils/parentFolderPath'
import { isInsideFolder } from '../../../enviroment/utils/isInsideFolder'
import { useDataRoomUpload } from '../../../enviroment/hooks/useDataRoomUpload'

const StorageUsageBar: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const navigate = useNavigate()
  const currentPlan = useTenantStore((state) => state.currentTenant?.plan)
  const isEnterprise = currentPlan === SubscriptionPlanType.ENTERPRISE

  const { data: storageUsage, isLoading } = api.project.dataRoom.storageUsage.useQuery({
    enabled: isOpen && !isEnterprise,
  })

  if (isEnterprise) return null

  return (
    <>
      {isLoading ? (
        <>
          <div className="mt-0.5 h-1.5 w-20 animate-pulse rounded-full bg-gray-700" />
          <div className="mt-0.5 h-3 w-24 animate-pulse rounded bg-gray-700" />
        </>
      ) : storageUsage?.limitBytes != null ? (
        <>
          <div className="mt-0.5 h-1.5 w-20 overflow-hidden rounded-full bg-gray-700">
            <div
              className="h-full rounded-full bg-light-green-500 transition-all duration-300"
              style={{ width: `${Math.min((storageUsage.usedBytes / storageUsage.limitBytes) * 100, 100)}%` }}
            />
          </div>
          <Typography variant="sm" className="mt-0.5 text-gray-200">
            {formatFileSize(storageUsage.usedBytes)} / {formatFileSize(storageUsage.limitBytes)}
          </Typography>
        </>
      ) : null}
      <Typography
        variant="sm"
        className="mt-0.5 cursor-pointer text-light-green-500 underline hover:text-light-green-400"
        onClick={() => navigate(ROUTES.SUBS)}
      >
        Upgrade Tier
      </Typography>
    </>
  )
}

interface DataRoomDialogProps {
  isOpen: boolean
  onClose: () => void
  project: Project
}

const DataRoomDialog: React.FC<DataRoomDialogProps> = ({ isOpen, onClose, project }) => {
  const projectId = project.id
  const { isSuperAdmin } = useAuth()
  const dataRoomActionsEnabled = !(project.isPublic && !isSuperAdmin)

  const [selectedFolderPath, setSelectedFolderPath] = useState<string | null>(null)
  const { data: foldersData, isLoading: isFoldersLoading } = api.project.dataRoom.folders.useQuery(
    projectId,
    selectedFolderPath,
  )
  // The sidebar always lists the top level, while the main pane follows the open folder
  const { data: rootFoldersData, isLoading: isRootFoldersLoading } = api.project.dataRoom.folders.useQuery(
    projectId,
    null,
  )
  const folders = foldersData?.data ?? []
  const rootFolders = rootFoldersData?.data ?? []
  const totalDocuments = rootFoldersData?.documentCount ?? 0
  const openFolderFileCount = foldersData?.fileCount ?? 0
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDocument, setSelectedDocument] = useState<DataRoomDocument | null>(null)

  const openFolder = useCallback((path: string | null) => {
    setSelectedFolderPath(path)
    setSelectedDocument(null)
  }, [])
  const upload = useDataRoomUpload(projectId, isOpen)
  const isKnowledgeBaseRefreshing = upload.isUploading
  const dropzoneRef = useRef<DataRoomFileDropzoneHandle>(null)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const isSelecting = selectedIds.size > 0
  const [isMoveToFolderOpen, setIsMoveToFolderOpen] = useState(false)

  const { mutateAsync: deleteFiles, isPending: isDeleting } = api.project.dataRoom.deleteFiles.useMutation()
  const [folderToDelete, setFolderToDelete] = useState<DataRoomFolder | null>(null)
  const { mutateAsync: renameFolder } = api.project.dataRoom.renameFolder.useMutation({
    onSuccess: (_, { folderPath, newFolderName }) => {
      if (!isInsideFolder(selectedFolderPath, folderPath)) return
      // The open folder moves with the rename, whether it is the renamed one or a descendant
      const renamedPath = joinFolderPath(parentFolderPath(folderPath), newFolderName)
      openFolder(renamedPath + (selectedFolderPath as string).slice(folderPath.length))
    },
  })

  const handleRenameFolder = useCallback(
    async (folder: DataRoomFolder, newName: string) => {
      const result = folderNameSchema.safeParse(newName)
      if (!result.success) {
        enqueueSnackbar(result.error.issues[0].message, { variant: 'error' })
        return
      }
      await renameFolder({ projectId, folderPath: folder.path, newFolderName: result.data })
    },
    [renameFolder, projectId],
  )

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => setSelectedIds(new Set()), [])

  const handleDelete = useCallback(async () => {
    await deleteFiles({ projectId: project.id, documentIds: Array.from(selectedIds) })
    handleClearSelection()
    // reset selected document if it's among the deleted ones
    setSelectedDocument((prev) => (prev && selectedIds.has(prev.id) ? null : prev))
  }, [selectedIds, project.id, deleteFiles, handleClearSelection])

  const handleClose = useCallback(() => {
    handleClearSelection()
    onClose()
  }, [onClose, handleClearSelection])

  useEffect(() => {
    if (isOpen) {
      setSelectedFolderPath(null)
      setSearchTerm('')
      setSelectedDocument(null)
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className="pointer-events-none" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-[50%] top-[50%] z-50 flex h-[90vh] w-[90vw] max-w-[1600px] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg border border-gray-500 bg-gray-900 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
        >
          <DialogPrimitive.Title className="sr-only">Project Data Room</DialogPrimitive.Title>
          {/* Here, not in the list: the list unmounts while a document is open, and the header buttons need the pickers */}
          {dataRoomActionsEnabled && (
            <DataRoomFileDropzone
              ref={dropzoneRef}
              onFiles={(files) => upload.uploadFiles(files, selectedFolderPath)}
              onFolder={(files) => upload.planFolder(files, selectedFolderPath)}
            />
          )}
          <div className="fixed inset-0 z-[-1]" onClick={handleClose} />
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-500 px-6 py-4">
            <div>
              <Typography variant="lg-bold" className="text-gray-50">
                Project Data Room
              </Typography>
              <div className="flex items-center gap-2">
                <Typography variant="sm" className="mt-0.5 text-gray-200">
                  {project.name}
                </Typography>
                <Typography variant="sm" className="mt-0.5 text-gray-200">
                  &#8226;
                </Typography>
                <Typography variant="sm" className="mt-0.5 text-gray-200">
                  {totalDocuments} documents
                </Typography>
                <StorageUsageBar isOpen={isOpen} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              {dataRoomActionsEnabled && (
                <>
                  <GradientBorderButton
                    gradientSize="sm"
                    textSize="sm"
                    onClick={() => dropzoneRef.current?.openFolder()}
                    disabled={isKnowledgeBaseRefreshing}
                  >
                    <IconFolderUp className="h-4 w-4" />
                    {upload.folderProgressLabel ?? 'Upload Folder'}
                  </GradientBorderButton>
                  <PrimaryButton
                    size="sm"
                    leftIcon={<IconUpload className="h-4 w-4" />}
                    onClick={() => dropzoneRef.current?.open()}
                    disabled={isKnowledgeBaseRefreshing}
                  >
                    Upload File
                  </PrimaryButton>
                </>
              )}
              <button
                onClick={handleClose}
                className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-700 hover:text-gray-50"
              >
                <IconX className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-1 gap-8 overflow-hidden px-8">
            <DataRoomSidebar
              folders={rootFolders}
              totalDocuments={totalDocuments}
              isLoading={isRootFoldersLoading}
              selectedFolderPath={selectedFolderPath}
              onSelectFolder={(path) => {
                openFolder(path)
              }}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              projectId={projectId}
              onRenameFolder={handleRenameFolder}
              onDeleteFolder={setFolderToDelete}
              actionsEnabled={dataRoomActionsEnabled}
            />

            {selectedDocument ? (
              <DataRoomDocumentDetail
                document={selectedDocument}
                projectId={projectId}
                projectName={project.name}
                onBack={() => setSelectedDocument(null)}
              />
            ) : (
              <DataRoomDocumentsList
                projectId={projectId}
                folderPath={selectedFolderPath}
                isFoldersLoading={isFoldersLoading}
                fileCount={openFolderFileCount}
                folders={folders}
                onSelectFolder={(path) => {
                  openFolder(path)
                }}
                onSelectDocument={setSelectedDocument}
                actionsEnabled={dataRoomActionsEnabled}
                pendingFiles={upload.pendingFiles}
                onUpload={dataRoomActionsEnabled ? () => dropzoneRef.current?.open() : undefined}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                isDeleting={isDeleting}
              />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>

      {isSelecting && (
        <DataRoomActionPanel
          selectedCount={selectedIds.size}
          isDeleting={isDeleting}
          availableFolderCount={selectedFolderPath ? 1 : folders.length}
          onDelete={handleDelete}
          onClear={handleClearSelection}
          onMoveToFolder={() => setIsMoveToFolderOpen(true)}
        />
      )}

      <DeleteFolderModal
        isOpen={folderToDelete !== null}
        onClose={() => setFolderToDelete(null)}
        projectId={projectId}
        folderPath={folderToDelete?.path ?? ''}
        folderName={folderToDelete?.name ?? ''}
        documentCount={folderToDelete?.documentCount ?? 0}
        onSelectFolder={openFolder}
        selectedFolderPath={selectedFolderPath}
      />

      <UploadFolderModal
        plan={upload.folderPlan}
        rootExists={folders.some((folder) => folder.path === upload.folderPlan?.rootPath)}
        onConfirm={upload.confirmFolder}
        onCancel={upload.cancelFolder}
      />

      {upload.storageLimit && (
        <StorageLimitExceededDialog
          open
          usedBytes={upload.storageLimit.usedBytes}
          limitBytes={upload.storageLimit.limitBytes}
          onOpenChange={(open) => {
            if (!open) upload.closeStorageLimit()
          }}
        />
      )}

      <MoveToFolderModal
        isOpen={isMoveToFolderOpen}
        onClose={() => {
          setIsMoveToFolderOpen(false)
          handleClearSelection()
        }}
        onMoveSuccess={(targetFolderPath) => {
          setIsMoveToFolderOpen(false)
          handleClearSelection()
          openFolder(targetFolderPath)
        }}
        projectId={projectId}
        selectedIds={Array.from(selectedIds)}
        currentFolderPath={selectedFolderPath}
      />
    </Dialog>
  )
}

export default DataRoomDialog
